import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Button,
  Icon,
  Label,
  Message,
  Form,
  Dropdown,
  Confirm,
  Grid,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../../api';

export default function ContributorDetail() {
  const { id } = useParams();
  const [contributor, setContributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add language
  const [addLangCode, setAddLangCode] = useState('');
  const [addLangError, setAddLangError] = useState(null);

  // Confirm dialogs
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmRevokeLang, setConfirmRevokeLang] = useState(null);

  // Available languages
  const [availableLanguages, setAvailableLanguages] = useState([]);

  useEffect(() => {
    loadContributor();
    loadLanguages();
  }, [id]);

  async function loadContributor() {
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiPath()}/@polyglot/contributors/${id}`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (!res.ok) throw new Error('Failed to load contributor');
      const data = await res.json();
      setContributor(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function loadLanguages() {
    try {
      const res = await fetch(`${getApiPath()}/@polyglot/coverage`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const langs = [...new Set((data.items || []).map((i) => i.language))].sort();
        setAvailableLanguages(langs.map((l) => ({ key: l, value: l, text: l })));
      }
    } catch {
      // non-critical
    }
  }

  async function handleToggleActive() {
    try {
      await fetch(`${getApiPath()}/@polyglot/contributors/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ active: !contributor.active }),
      });
      setConfirmDeactivate(false);
      loadContributor();
    } catch {
      // ignore
    }
  }

  async function handleAddLanguage() {
    if (!addLangCode) {
      setAddLangError('Select a language.');
      return;
    }
    setAddLangError(null);
    try {
      const res = await fetch(
        `${getApiPath()}/@polyglot/contributors/${id}/languages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ language_code: addLangCode }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setAddLangError(body.error || 'Failed to add language.');
        return;
      }
      setAddLangCode('');
      loadContributor();
    } catch (err) {
      setAddLangError(err.message);
    }
  }

  async function handleRevokeLanguage(code) {
    try {
      await fetch(
        `${getApiPath()}/@polyglot/contributors/${id}/languages/${code}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      setConfirmRevokeLang(null);
      loadContributor();
    } catch {
      // ignore
    }
  }

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '200px' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message error>{error}</Message>
      </Container>
    );
  }

  if (!contributor) return null;

  const activeLanguages = (contributor.languages || []).filter((l) => !l.revoked_at);
  const revokedLanguages = (contributor.languages || []).filter((l) => l.revoked_at);

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title={`Contributor: ${contributor.fullname}`} />

      <Link to="/polyglot/contributors">
        <Icon name="arrow left" /> Back to Contributors
      </Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        <Icon name="user" />
        <Header.Content>
          {contributor.fullname}
          <Header.Subheader>{contributor.email}</Header.Subheader>
        </Header.Content>
        <Label
          color={contributor.active ? 'green' : 'red'}
          style={{ marginLeft: '1em' }}
        >
          {contributor.active ? 'Active' : 'Inactive'}
        </Label>
      </Header>

      {/* Profile */}
      <Grid stackable columns={2}>
        <Grid.Column>
          <Segment>
            <Header as="h3">Profile</Header>
            <Table definition compact>
              <Table.Body>
                <Table.Row>
                  <Table.Cell width={6}>Username</Table.Cell>
                  <Table.Cell>{contributor.nick_user_id}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Native Languages</Table.Cell>
                  <Table.Cell>
                    {(contributor.native_languages || []).map((l) => (
                      <Label key={l} size="small">{l}</Label>
                    ))}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Member Since</Table.Cell>
                  <Table.Cell>
                    {new Date(contributor.created_at).toLocaleDateString()}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Total Contributions</Table.Cell>
                  <Table.Cell>{contributor.total_contributions}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column>
          <Segment>
            <Header as="h3">Qualifications</Header>
            {contributor.proficiency && (
              <Table compact size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Language</Table.HeaderCell>
                    <Table.HeaderCell>Level</Table.HeaderCell>
                    <Table.HeaderCell>Years</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.entries(contributor.proficiency).map(([lang, info]) => (
                    <Table.Row key={lang}>
                      <Table.Cell>{lang}</Table.Cell>
                      <Table.Cell>{info.level || '-'}</Table.Cell>
                      <Table.Cell>{info.years || '-'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
            {contributor.professional_experience && (
              <p style={{ marginTop: '1em' }}>
                <strong>Experience:</strong> {contributor.professional_experience}
              </p>
            )}
          </Segment>
        </Grid.Column>
      </Grid>

      {/* Certification */}
      <Segment>
        <Header as="h3">Declarations</Header>
        <Grid stackable columns={3}>
          <Grid.Column>
            <Message size="small">
              <Message.Header>Self-Certification</Message.Header>
              <p>{contributor.certification_text}</p>
              <p><em>Signed: {new Date(contributor.certified_at).toLocaleString()}</em></p>
            </Message>
          </Grid.Column>
          <Grid.Column>
            <Message size="small">
              <Message.Header>Training</Message.Header>
              <p>Completed: {contributor.training_completed_at
                ? new Date(contributor.training_completed_at).toLocaleString()
                : 'Not yet'}</p>
              {contributor.training_evidence && (
                <p>Evidence: {contributor.training_evidence}</p>
              )}
            </Message>
          </Grid.Column>
          <Grid.Column>
            <Message size="small">
              <Message.Header>Contributors Agreement</Message.Header>
              <p>{contributor.agreement_text}</p>
              <p><em>Signed: {new Date(contributor.agreement_signed_at).toLocaleString()}</em></p>
            </Message>
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Language Assignments */}
      <Segment>
        <Header as="h3">Language Assignments</Header>
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Language</Table.HeaderCell>
              <Table.HeaderCell>Assigned By</Table.HeaderCell>
              <Table.HeaderCell>Since</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {activeLanguages.map((l) => (
              <Table.Row key={l.code} positive>
                <Table.Cell>
                  <Label color="blue">{l.code}</Label>
                </Table.Cell>
                <Table.Cell>{l.assigned_by}</Table.Cell>
                <Table.Cell>{new Date(l.assigned_at).toLocaleDateString()}</Table.Cell>
                <Table.Cell>
                  <Button
                    size="mini"
                    color="red"
                    basic
                    onClick={() => setConfirmRevokeLang(l.code)}
                  >
                    Revoke
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            {revokedLanguages.map((l) => (
              <Table.Row key={l.code} disabled>
                <Table.Cell>
                  <Label>{l.code}</Label> <em>(revoked)</em>
                </Table.Cell>
                <Table.Cell>{l.assigned_by}</Table.Cell>
                <Table.Cell>{new Date(l.assigned_at).toLocaleDateString()}</Table.Cell>
                <Table.Cell>
                  <em>Revoked {new Date(l.revoked_at).toLocaleDateString()}</em>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {/* Add language */}
        <Form style={{ marginTop: '1em' }}>
          {addLangError && <Message error size="small">{addLangError}</Message>}
          <Form.Group inline>
            <Form.Field>
              <Dropdown
                placeholder="Add language"
                search
                selection
                options={availableLanguages}
                value={addLangCode}
                onChange={(e, { value }) => setAddLangCode(value)}
              />
            </Form.Field>
            <Button primary size="small" onClick={handleAddLanguage}>
              <Icon name="plus" /> Add Language
            </Button>
          </Form.Group>
        </Form>
      </Segment>

      {/* Admin Actions */}
      <Segment>
        <Header as="h3">Admin Actions</Header>
        <Button
          color={contributor.active ? 'red' : 'green'}
          onClick={() => setConfirmDeactivate(true)}
        >
          <Icon name={contributor.active ? 'ban' : 'check'} />
          {contributor.active ? 'Deactivate Contributor' : 'Reactivate Contributor'}
        </Button>
      </Segment>

      {/* Confirm dialogs */}
      <Confirm
        open={confirmDeactivate}
        content={`Are you sure you want to ${contributor.active ? 'deactivate' : 'reactivate'} ${contributor.fullname}?`}
        onCancel={() => setConfirmDeactivate(false)}
        onConfirm={handleToggleActive}
      />
      <Confirm
        open={!!confirmRevokeLang}
        content={`Revoke ${confirmRevokeLang} access for ${contributor.fullname}?`}
        onCancel={() => setConfirmRevokeLang(null)}
        onConfirm={() => handleRevokeLanguage(confirmRevokeLang)}
      />
    </Container>
  );
}
